import { Document } from '@tiptap/extension-document';
import { Bold } from '@tiptap/extension-bold';
import {
  BulletList,
  ListItem,
  ListKeymap,
  OrderedList
} from '@tiptap/extension-list';
import { Paragraph } from '@tiptap/extension-paragraph';
import { Text } from '@tiptap/extension-text';
import { Italic } from '@tiptap/extension-italic';
import { Link } from '@tiptap/extension-link';
import { Underline } from '@tiptap/extension-underline';
import { UndoRedo } from '@tiptap/extensions';
import { useEffect, useMemo, useRef } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import { useReduxState } from 'hooks';
import {
  CodeExtensionWithKeyboardShortcuts,
  RichTextEditorMenu
} from 'components/common/richTextEditor';

export interface ListEditorHandle {
  getTypeAndStart: () => { type: 'ol_list' | 'ul_list'; start: number };
  decreaseIndentation: () => void;
  increaseIndentation: () => void;
  highlightList: (bool: boolean) => void;
  listStartChange: (value: number) => void;
  toggleType: (type: 'ol_list' | 'ul_list') => void;
}

// extend storage with a disabled property to handle disabling
// the native keyboard shortcuts when filling a language version
declare module '@tiptap/core' {
  interface Storage {
    listItem: { disabled: boolean };
  }
}

const SingleLineParagraph = Paragraph.extend({
  addKeyboardShortcuts() {
    return {
      Enter: ({ editor }) => {
        const { state } = editor;
        const { selection } = state;
        const { $from } = selection;

        // Get the current paragraph node
        const paragraphNode = $from.parent;

        // Check if the paragraph is empty (no text content)
        const isEmpty = paragraphNode.textContent.trim() === '';
        if (isEmpty) return true;

        // Allow default behavior for non-empty paragraphs
        return editor.chain().focus().splitListItem('listItem').run();
      }
    };
  }
});

const extendListAttributes = (type: 'ordered' | 'bullet') => ({
  listStyleType: {
    default: type === 'ordered' ? 'decimal' : 'disc',
    parseHTML: (element) => {
      const style = element.getAttribute('style');
      if (!style) return null;

      const match = style.match(/list-style-type\s*:\s*([^;]+)/i);
      return match ? match[1].trim() : null;
    },
    renderHTML: (attrs) =>
      attrs.listStyleType
        ? { style: `list-style-type: ${attrs.listStyleType}` }
        : {}
  },
  class: {
    default: ''
  }
});

const getNextListType = (currentItem: string, isBulletList: boolean) => {
  const items = isBulletList
    ? ['disc', 'circle', 'square']
    : ['decimal', 'lower-alpha', 'upper-alpha', 'lower-roman', 'upper-roman'];

  const currentIndex = items.indexOf(currentItem);
  return items[currentIndex + 1] || items[0];
};

interface Props {
  content: string;
  disabled?: boolean;
  getHandler: (handler: ListEditorHandle) => void;
  onChange?: (html: string) => void;
  setSelectionListType: (type: 'ol_list' | 'ul_list') => void;
}

const ListEditor = ({
  content,
  disabled = false,
  onChange,
  getHandler,
  setSelectionListType
}: Props) => {
  const { theme } = useReduxState();

  const lastEmittedRef = useRef<string | null>(null);
  const listElementRef = useRef<HTMLDivElement>(null);
  const updateValueTimeout = useRef<NodeJS.Timeout>(undefined);

  const editor = useEditor({
    editorProps: { attributes: { class: 'vuo-list-editor' } },
    extensions: [
      Document.extend({ content: 'list' }),
      Bold,
      BulletList.extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            ...extendListAttributes('bullet')
          };
        }
      }),
      CodeExtensionWithKeyboardShortcuts,
      Italic,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { rel: null, target: null }
      }),
      ListItem.extend({
        addKeyboardShortcuts() {
          return {
            Tab: ({ editor }) => {
              if (editor.storage.listItem.disabled) return true;
              editor.chain().focus().sinkListItem('listItem').run();
              return true;
            },
            'Shift-Tab': ({ editor }) => {
              if (editor.storage.listItem.disabled) return true;
              editor.chain().focus().liftListItem('listItem').run();
              return true;
            }
          };
        },
        addStorage() {
          return { disabled: false };
        }
      }),
      ListKeymap,
      OrderedList.extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            ...extendListAttributes('ordered')
          };
        }
      }),
      SingleLineParagraph,
      Text,
      Underline,
      UndoRedo
    ],
    content,
    immediatelyRender: false,
    onUpdate({ editor, transaction }) {
      // avoid updating the HTML because of the highlight
      if (transaction.getMeta('skipChange')) return;

      const editorValue = editor.getHTML();
      if (content === editorValue) return;

      clearTimeout(updateValueTimeout.current);
      updateValueTimeout.current = setTimeout(() => {
        lastEmittedRef.current = editorValue;
        onChange(editorValue);
      }, 500);
    }
  });

  useEffect(() => {
    return () => {
      clearTimeout(updateValueTimeout.current);
    };
  }, []);

  useEffect(() => {
    if (!editor) return;
    editor.storage.listItem.disabled = disabled;
  }, [editor, disabled]);

  useEffect(() => {
    if (!editor) return;

    editor.on('selectionUpdate', () => {
      setSelectionListType(
        editor.isActive('orderedList') ? 'ol_list' : 'ul_list'
      );
    });

    return () => {
      editor.off('selectionUpdate');
    };
  }, [editor, setSelectionListType]);

  useEffect(() => {
    if (!editor) return;
    if (content === undefined) return;

    if (content === lastEmittedRef.current) {
      lastEmittedRef.current = null;
      return;
    }

    if (content !== editor.getHTML()) {
      editor.commands.setContent(content, { emitUpdate: false });
      lastEmittedRef.current = null;
    }
  }, [content, editor]);

  const handler = useMemo(
    () => ({
      getTypeAndStart: () => {
        if (!editor) return { type: 'ul_list' as const, start: 1 };
        const isOrderedList = editor.isActive('orderedList');
        return {
          type: isOrderedList ? ('ol_list' as const) : ('ul_list' as const),
          start: isOrderedList ? editor.getAttributes('orderedList').start : 1
        };
      },
      decreaseIndentation: () => {
        if (!editor) return;
        editor.chain().focus().liftListItem('listItem').run();
      },
      increaseIndentation: () => {
        if (!editor) return;
        editor.chain().focus().sinkListItem('listItem').run();
      },
      listStartChange: (value: number) => {
        if (!editor) return;
        editor
          .chain()
          .focus()
          .updateAttributes('orderedList', { start: value })
          .run();
      },
      highlightList: (bool: boolean) => {
        if (!editor) return;

        const transaction = editor.state.tr;

        // first we remove the focus class from
        // any lists within this rendered element
        editor.state.doc.descendants((node, pos) => {
          if (
            node.type.name === 'bulletList' ||
            node.type.name === 'orderedList'
          ) {
            const attributes = { ...node.attrs };
            delete attributes.class;

            transaction.setNodeMarkup(pos, null, attributes);
          }
        });

        if (bool) {
          const listType = editor.isActive('orderedList')
            ? 'orderedList'
            : 'bulletList';

          // walk up selection until we find a list node
          const { $from } = editor.state.selection;
          for (let i = $from.depth; i >= 0; i--) {
            const node = $from.node(i);
            if (node.type.name === listType) {
              transaction.setNodeMarkup($from.before(i), null, {
                ...node.attrs,
                class: 'highlight'
              });
              break;
            }
          }
        }

        transaction.setMeta('skipChange', true);
        editor.view.dispatch(transaction);
      },
      toggleType: (type: 'ol_list' | 'ul_list') => {
        if (!editor) return;

        const changeToBulletList = type === 'ul_list';
        const isBulletList = editor.isActive('bulletList');

        const { listStyleType } = editor.getAttributes(
          isBulletList ? 'bulletList' : 'orderedList'
        );
        const nextListType = getNextListType(listStyleType, isBulletList);

        const updateStyleType = (type: 'bulletList' | 'orderedList') =>
          editor
            .chain()
            .focus()
            .updateAttributes(type, { listStyleType: nextListType })
            .run();

        if (isBulletList) {
          return changeToBulletList
            ? updateStyleType('bulletList')
            : editor.chain().focus().toggleOrderedList().run();
        }

        return changeToBulletList
          ? editor.chain().focus().toggleBulletList().run()
          : updateStyleType('orderedList');
      }
    }),
    [editor]
  );

  useEffect(() => {
    if (handler && getHandler) getHandler(handler);
  }, [handler, getHandler]);

  return (
    <div className="list-editor" ref={listElementRef}>
      <EditorContent editor={editor} />

      {editor ? (
        <RichTextEditorMenu
          editor={editor}
          richTextElementRef={listElementRef}
        />
      ) : null}

      <style jsx global>{`
        .vuo-list-editor {
          background-color: white;
          border-radius: ${theme.inputBorderRadius};
          color: ${theme.textColor};
          font-family: inherit;
          font-size: 1.6rem;
        }
        .vuo-list-editor:focus {
          outline: none;
        }
        .vuo-list-editor ul,
        .vuo-list-editor ol {
          display: block;
          padding-inline-start: 40px;
          unicode-bidi: isolate;
        }
        .vuo-list-editor ul.highlight,
        .vuo-list-editor ol.highlight {
          background-color: ${theme.lightGrey} !important;
          border-radius: 8px;
        }
        .vuo-list-editor a {
          color: ${theme.linkColor};
        }
        .vuo-list-editor code {
          background-color: ${theme.lightGreyLighter};
          border-radius: 4px;
          font-size: 1.4rem;
          padding: 2px 4px;
        }
      `}</style>
    </div>
  );
};

export default ListEditor;
